export const htmlToPlainText = (html: string): string => {
	// Clean up the text:
	// 1. Remove HTML tags
	// 2. Decode HTML entities
	// 3. Replace multiple spaces with single space
	// 4. Remove leading/trailing whitespace
	return html
		.replace(/<[^>]*>/g, '') // Remove HTML tags
		.replace(/&nbsp;/g, ' ')
		.replace(/&amp;/g, '&')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/&apos;/g, "'")
		.replace(/&copy;/g, '©')
		.replace(/&reg;/g, '®')
		.replace(/&trade;/g, '™')
		.replace(/&euro;/g, '€')
		.replace(/&pound;/g, '£')
		.replace(/&yen;/g, '¥')
		.replace(/&cent;/g, '¢')
		.replace(/&sect;/g, '§')
		.replace(/&para;/g, '¶')
		.replace(/&middot;/g, '·')
		.replace(/&hellip;/g, '…')
		.replace(/&ndash;/g, '–')
		.replace(/&mdash;/g, '—')
		.replace(/&lsquo;/g, "'")
		.replace(/&rsquo;/g, "'")
		.replace(/&ldquo;/g, '"')
		.replace(/&rdquo;/g, '"')
		.replace(/&bull;/g, '•')
		.replace(/&deg;/g, '°')
		.replace(/&plusmn;/g, '±')
		.replace(/&times;/g, '×')
		.replace(/&divide;/g, '÷')
		.replace(/&frac12;/g, '½')
		.replace(/&frac14;/g, '¼')
		.replace(/&frac34;/g, '¾')
		.replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(parseInt(dec, 10))) // Numeric entities
		.replace(/&#x([0-9a-f]+);/gi, (match, hex) => String.fromCharCode(parseInt(hex, 16))) // Hex entities
		.replace(/\s+/g, ' ')
		.replace(/\n\s*\n/g, '\n')
		.trim()
}

export const createExcerpt = (content: string, maxLength: number = 200): string => {
	const plainText = htmlToPlainText(content)
	if (plainText.length <= maxLength) {
		return plainText
	}
	// Find the last space before maxLength to avoid cutting words
	const truncated = plainText.slice(0, maxLength)
	const lastSpace = truncated.lastIndexOf(' ')
	return lastSpace > 0 
		? truncated.slice(0, lastSpace) + '...'
		: truncated + '...'
}

export const cleanBlogContent = (html: string): string => {
	if (!html) return html

	let cleanedHtml = html

	// Remove p tag with class "primaryMeta" and its content
	cleanedHtml = cleanedHtml.replace(
		/<p[^>]*class="primaryMeta"[^>]*>.*?<\/p>/gi,
		''
	)

	// Add target="_blank" and rel="noopener noreferrer" only to external domain links
	cleanedHtml = cleanedHtml.replace(
		/<a\s+([^>]*?)>/gi,
		(match, attributes) => {
			// Extract href attribute
			const hrefMatch = attributes.match(/href="([^"]*)"/)
			if (!hrefMatch) {
				return match // No href, return as is
			}

			const href = hrefMatch[1]
			
			// Check if it's an external domain link
			let isExternalDomain = false
			
			if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('//')) {
				try {
					// Get current domain from window.location or use a fallback
					const currentDomain = typeof window !== 'undefined' 
						? window.location.hostname 
						: process.env.NEXT_PUBLIC_SITE_URL 
							? new URL(process.env.NEXT_PUBLIC_SITE_URL).hostname
							: 'localhost'
					
					// Get main domain from environment variable
					const mainDomain = process.env.NEXT_PUBLIC_MAIN_DOMAIN
					
					// Parse the link URL
					const linkUrl = new URL(href.startsWith('//') ? `https:${href}` : href)
					const linkDomain = linkUrl.hostname
					
					// Check if domains are different (excluding main domain)
					isExternalDomain = linkDomain !== currentDomain && 
						(!mainDomain || linkDomain !== mainDomain)
				} catch (error) {
					// If URL parsing fails, treat as external for safety
					isExternalDomain = true
				}
			} else if (href.startsWith('mailto:') || href.startsWith('tel:')) {
				// Email and phone links are always external
				isExternalDomain = true
			}

			// If it's not an external domain, return as is
			if (!isExternalDomain) {
				return match
			}

			// Check if target="_blank" already exists
			if (attributes.includes('target="_blank"')) {
				return match
			}
			
			// Add target="_blank" and rel="noopener noreferrer" to external domain links
			const relMatch = attributes.match(/rel="([^"]*)"/)
			if (relMatch) {
				// If rel already exists, add noopener noreferrer to it
				const existingRel = relMatch[1]
				const newRel = existingRel.includes('noopener') && existingRel.includes('noreferrer') 
					? existingRel 
					: `${existingRel} noopener noreferrer`.trim()
				return `<a ${attributes.replace(/rel="[^"]*"/, `rel="${newRel}"`)} target="_blank">`
			} else {
				// If no rel attribute, add both target and rel
				return `<a ${attributes} target="_blank" rel="noopener noreferrer">`
			}
		}
	)

	// Clean up any extra whitespace that might be left
	cleanedHtml = cleanedHtml.replace(/\s+/g, ' ').trim()

	return cleanedHtml
}
